import { useState } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock, WireImageArtifact, WirePhotoAspectRatio } from '../../api/types'
import { PhotoItemEditor } from './PhotoGroupBlockPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

type PhotoBlock = Extract<WireArticleBlock, { type: 'photo' }>

// ─── Option tables ────────────────────────────────────────────────────────────

const WIDTH_OPTIONS: { value: NonNullable<PhotoBlock['width']>; label: string }[] = [
  { value: 'column',      label: 'Column'  },
  { value: 'wide',        label: 'Wide'    },
  { value: 'full',        label: 'Full'    },
  { value: 'bleed',       label: 'Bleed'   },
  { value: 'float-left',  label: '← Float' },
  { value: 'float-right', label: 'Float →' },
]

const RATIO_OPTIONS: { value: WirePhotoAspectRatio; label: string }[] = [
  { value: 'natural', label: 'Natural' },
  { value: '1:1',     label: '1:1'     },
  { value: '4:3',     label: '4:3'     },
  { value: '3:2',     label: '3:2'     },
  { value: '16:9',    label: '16:9'    },
  { value: '2:1',     label: '2:1'     },
  { value: '3:4',     label: '3:4'     },
  { value: '2:3',     label: '2:3'     },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

// ─── Artifact picker ──────────────────────────────────────────────────────────

function ArtifactPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireImageArtifact[]
  onSelect: (a: WireImageArtifact) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Change from artifacts
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      {artifacts.length === 0 ? (
        <p className="px-2 py-3 text-[11px] text-fg-secondary">No image artifacts uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5 p-2">
          {artifacts.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="overflow-hidden rounded border border-line bg-fill transition-colors hover:border-accent-primary-solid"
              title={a.filename}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={a.presignedUrl}
                  alt={a.altText ?? ''}
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="truncate px-1 py-0.5 text-[9px] text-fg-secondary">{a.filename}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  block: PhotoBlock
  onUpdate: (updated: PhotoBlock) => void
  imageArtifacts: WireImageArtifact[]
}

export function PhotoBlockPanel({ block, onUpdate, imageArtifacts }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const width       = block.width       ?? 'column'
  const aspectRatio = block.aspectRatio ?? 'natural'

  function patchBlock(patch: Partial<Pick<PhotoBlock, 'width' | 'aspectRatio'>>) {
    onUpdate({ ...block, ...patch })
  }

  function selectArtifact(artifact: WireImageArtifact) {
    onUpdate({
      ...block,
      photo: {
        id: block.photo.id,
        artifactId: artifact.id,
        classification: artifact.classification,
        captionClassification: artifact.captionClassification,
        captionDisseminationControls: artifact.captionDisseminationControls,
        caption: artifact.caption,
        altText: artifact.altText,
        credit: artifact.credit,
        focalPoint: artifact.focalPoint,
        expandable: block.photo.expandable,
      },
    })
    setPickerOpen(false)
  }

  return (
    <div className="space-y-4">

      {/* Image slot — Change button + artifact picker + PhotoItemEditor */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Image</FieldLabel>
          <button
            type="button"
            onClick={() => setPickerOpen(o => !o)}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            Change
          </button>
        </div>
        {pickerOpen && (
          <div className="mb-2">
            <ArtifactPicker
              artifacts={imageArtifacts}
              onSelect={selectArtifact}
              onClose={() => setPickerOpen(false)}
            />
          </div>
        )}
        <PhotoItemEditor
          photo={block.photo}
          index={0}
          imageUrl={imageArtifacts.find(a => a.id === block.photo.artifactId)?.presignedUrl ?? ''}
          onChange={photo => onUpdate({ ...block, photo })}
        />
      </div>

      {/* Width */}
      <div>
        <FieldLabel>Width</FieldLabel>
        <div className="flex flex-wrap gap-1">
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => patchBlock({ width: opt.value })}
              className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                width === opt.value
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect ratio */}
      <div>
        <FieldLabel>Aspect Ratio</FieldLabel>
        <select
          value={aspectRatio}
          onChange={e => patchBlock({ aspectRatio: e.target.value as WirePhotoAspectRatio })}
          className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-line focus:outline-none"
        >
          {RATIO_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

    </div>
  )
}
